import { z } from "zod";

import { OpaqueIdSchema } from "./identity-schemas";
import { ServiceLevelsSchema } from "./support-service-levels";

// ADR-0089: the support desk's settings. Triage classifies a new ticket and leaves an internal
// summary note; draft replies save a reply for a person to review and send; reply targets say when
// an answer is due.

export const SUPPORT_AI_GATEWAYS = ["cloudflare", "vercel"] as const;
export const SupportAiGatewaySchema = z.enum(SUPPORT_AI_GATEWAYS);
export type SupportAiGateway = z.infer<typeof SupportAiGatewaySchema>;

export const DEFAULT_SUPPORT_AI_MODEL = "anthropic/claude-sonnet-5";

/** A model ID as both gateways name it, `author/model`, or a Workers AI `@cf/author/model`. */
export const SupportAiModelSchema = z
  .string()
  .trim()
  .min(3)
  .max(120)
  .regex(
    /^(?:@(?:cf|hf)\/)?[A-Za-z0-9][A-Za-z0-9._-]*\/[A-Za-z0-9][A-Za-z0-9._:-]*$/u,
    "Use a model ID such as anthropic/claude-sonnet-5.",
  );

export const SUPPORT_AUTOMATIONS = ["triage", "draft_reply"] as const;
export const SupportAutomationSchema = z.enum(SUPPORT_AUTOMATIONS);
export type SupportAutomation = z.infer<typeof SupportAutomationSchema>;

export const SupportAutomationTriggerSchema = z.enum(["ticket_created", "customer_message"]);
export type SupportAutomationTrigger = z.infer<typeof SupportAutomationTriggerSchema>;

export const SupportAutomationStatusSchema = z.enum([
  "queued",
  "running",
  "succeeded",
  "skipped",
  "failed",
]);
export type SupportAutomationStatus = z.infer<typeof SupportAutomationStatusSchema>;

const TimestampSchema = z.number().int().nonnegative();

export const SupportSettingsSchema = z.strictObject({
  aiGateway: SupportAiGatewaySchema,
  aiModel: z.string().min(3).max(120),
  triageEnabled: z.boolean(),
  draftsEnabled: z.boolean(),
  serviceLevels: ServiceLevelsSchema,
  updatedAt: TimestampSchema,
  updatedByName: z.string().max(160).nullable(),
});
export type SupportSettings = z.infer<typeof SupportSettingsSchema>;

export const SupportSettingsUpdateSchema = z
  .strictObject({
    aiGateway: SupportAiGatewaySchema.optional(),
    aiModel: SupportAiModelSchema.optional(),
    triageEnabled: z.boolean().optional(),
    draftsEnabled: z.boolean().optional(),
    serviceLevels: ServiceLevelsSchema.optional(),
  })
  .refine((update) => Object.values(update).some((value) => value !== undefined), {
    message: "Change at least one setting.",
  });
export type SupportSettingsUpdate = z.infer<typeof SupportSettingsUpdateSchema>;

export const SupportAutomationRunSchema = z.strictObject({
  id: OpaqueIdSchema,
  ticketId: OpaqueIdSchema,
  ticketReference: z.string().min(2).max(20),
  automation: SupportAutomationSchema,
  trigger: SupportAutomationTriggerSchema,
  status: SupportAutomationStatusSchema,
  gateway: SupportAiGatewaySchema,
  model: z.string().min(3).max(120),
  outcome: z.string().max(60).nullable(),
  errorCode: z.string().max(60).nullable(),
  inputTokens: z.number().int().nonnegative().nullable(),
  outputTokens: z.number().int().nonnegative().nullable(),
  createdAt: TimestampSchema,
  finishedAt: TimestampSchema.nullable(),
});
export type SupportAutomationRun = z.infer<typeof SupportAutomationRunSchema>;

/** Whether each gateway can be used: Cloudflare needs nothing, Vercel needs its key installed. */
export const SupportGatewayReadinessSchema = z.strictObject({
  cloudflare: z.boolean(),
  vercel: z.boolean(),
});

export const SupportAutomationOverviewSchema = z.strictObject({
  settings: SupportSettingsSchema,
  gatewaysReady: SupportGatewayReadinessSchema,
  runs: z.array(SupportAutomationRunSchema).max(50),
});
export type SupportAutomationOverview = z.infer<typeof SupportAutomationOverviewSchema>;
